import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Star, Trophy, TrendingUp, X } from 'lucide-react';

const FLAG_URL = (code) => `https://flagcdn.com/w80/${code?.toLowerCase()}.png`;

const statusColors = {
  pendiente: 'bg-[#F4E39E]/20 text-[#F4E39E]',
  'en juego': 'bg-green-500/20 text-green-400',
  finalizado: 'bg-white/10 text-white/50',
};

// Función para calcular puntos
const calculatePoints = (prediction, result) => {
  if (!prediction || !result) return 0;
  
  const { home_goals: predHome, away_goals: predAway } = prediction;
  const { home_goals: resHome, away_goals: resAway } = result;
  
  // 3 puntos por marcador exacto
  if (predHome === resHome && predAway === resAway) {
    return 3;
  }
  
  // 1 punto por resultado correcto (ganador o empate)
  const predResult = predHome > predAway ? 'home' : predHome < predAway ? 'away' : 'draw';
  const actualResult = resHome > resAway ? 'home' : resHome < resAway ? 'away' : 'draw';
  
  if (predResult === actualResult) {
    return 1;
  }
  
  return 0;
};

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [results, setResults] = useState({});
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState('all');
  const [statusFilter, setStatusFilter] = useState('todos'); // 'todos', 'pendientes', 'finalizados'
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/matches/enriched'), // Cambio: usar matches/enriched para obtener TODOS los partidos
      api.get('/predictions'), // Obtener todas las predicciones del usuario
      api.get('/results'),
      api.get('/stages'),
    ]).then(([m, p, r, s]) => {
      // Ordenar partidos por fecha y hora cronológicamente
      const sortedMatches = m.data.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateA - dateB;
      });
      
      setMatches(sortedMatches);
      setStages(s.data);
      
      // Mapear predicciones por match_id
      const predictionsMap = {};
      p.data.forEach(pred => {
        predictionsMap[pred.match_id] = {
          home_goals: pred.home_goals,
          away_goals: pred.away_goals
        };
      });
      setPredictions(predictionsMap);
      
      // Mapear resultados por match_id
      const resultsMap = {};
      r.data.forEach(res => {
        resultsMap[res.match_id] = { home_goals: res.home_goals, away_goals: res.away_goals };
      });
      setResults(resultsMap);
    }).finally(() => setLoading(false));
  }, []);

  const handleMatchClick = (match) => {
    if (match.status === 'finalizado') {
      setSelectedMatch(match);
      setShowResultModal(true);
    }
  };

  // Filtrar partidos
  let filtered = matches;
  
  // Filtro por etapa
  if (selectedStage !== 'all') {
    filtered = filtered.filter(m => m.stage?.id === selectedStage);
  }
  
  // Filtro por estado
  if (statusFilter === 'pendientes') {
    filtered = filtered.filter(m => m.status === 'pendiente' || m.status === 'en juego');
  } else if (statusFilter === 'finalizados') {
    filtered = filtered.filter(m => m.status === 'finalizado');
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="matches-page">
      <h2 className="text-2xl lg:text-3xl font-extrabold gold-text" style={{ fontFamily: 'Montserrat' }}>Partidos</h2>
      <p className="text-white/50 text-sm">Consulta todos los partidos del torneo</p>

      {/* Filtros */}
      <div className="flex flex-col lg:flex-row gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white lg:max-w-xs" data-testid="status-filter">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
            <SelectItem value="todos" className="text-white">Todos los partidos</SelectItem>
            <SelectItem value="pendientes" className="text-white">Pendientes por jugar</SelectItem>
            <SelectItem value="finalizados" className="text-white">Finalizados</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedStage} onValueChange={setSelectedStage}>
          <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white lg:max-w-xs" data-testid="stage-filter">
            <SelectValue placeholder="Todas las etapas" />
          </SelectTrigger>
          <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
            <SelectItem value="all" className="text-white">Todas las etapas</SelectItem>
            {stages.map(s => (
              <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando partidos...</div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">No hay partidos disponibles</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
          {filtered.map((match, i) => {
            const result = results[match.id];
            const prediction = predictions[match.id];
            const isFinalizado = match.status === 'finalizado';
            const points = isFinalizado && prediction ? calculatePoints(prediction, result) : null;
            
            return (
              <div 
                key={match.id} 
                className={`glass-card p-4 ${isFinalizado ? 'cursor-pointer hover:border-[#F4E39E]/50 transition-colors' : ''}`}
                data-testid={`match-card-${i}`}
                onClick={() => handleMatchClick(match)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#F4E39E] font-semibold">
                    {String(i + 1).padStart(2, '0')}. {match.stage?.name}
                  </span>
                  <div className="flex items-center gap-2">
                    {isFinalizado && points !== null && (
                      <Badge className="bg-[#F4E39E]/20 text-[#F4E39E] text-xs font-bold">
                        {points} pts
                      </Badge>
                    )}
                    <Badge className={`text-[10px] ${statusColors[match.status] || 'bg-white/10 text-white/50'}`}>
                      {match.status}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <img src={FLAG_URL(match.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold">{match.home_team?.code}</span>
                    {match.favorite_home && (
                      <Star size={14} className="text-[#FFD700] fill-[#FFD700]" title="Favorito" />
                    )}
                  </div>
                  
                  {isFinalizado && result ? (
                    <div className="flex items-center gap-2 px-4">
                      <span className="text-white font-black text-2xl" style={{ fontFamily: 'Montserrat' }}>
                        {result.home_goals}
                      </span>
                      <span className="text-white/30 font-bold">-</span>
                      <span className="text-white font-black text-2xl" style={{ fontFamily: 'Montserrat' }}>
                        {result.away_goals}
                      </span>
                    </div>
                  ) : (
                    <span className="gold-text font-black text-xl px-4" style={{ fontFamily: 'Montserrat' }}>VS</span>
                  )}
                  
                  <div className="flex items-center gap-2 flex-1 justify-end">
                    {match.favorite_away && (
                      <Star size={14} className="text-[#FFD700] fill-[#FFD700]" title="Favorito" />
                    )}
                    <span className="text-white font-bold">{match.away_team?.code}</span>
                    <img src={FLAG_URL(match.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                  </div>
                </div>

                <div className="text-white/40 text-xs mt-3 text-center space-y-0.5">
                  <div>Fecha: {match.date} | Hora: {match.time}</div>
                  <div>Sede: {match.venue?.city}, {match.stadium?.name}</div>
                  {isFinalizado && (
                    <div className="text-[#F4E39E] mt-2 text-xs font-semibold">
                      Click para ver detalles
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Resultado */}
      <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#F4E39E] flex items-center gap-2" style={{ fontFamily: 'Montserrat' }}>
              <Trophy size={20} />
              Resultado del Partido
            </DialogTitle>
          </DialogHeader>
          
          {selectedMatch && (
            <div className="space-y-4">
              {/* Etapa y fecha */}
              <div className="text-center">
                <p className="text-[#F4E39E] text-sm font-semibold">{selectedMatch.stage?.name}</p>
                <p className="text-white/50 text-xs">{selectedMatch.date} - {selectedMatch.time}</p>
              </div>

              {/* Marcador Final */}
              <div className="bg-[#0A0F1C] rounded-lg p-4 border border-[#F4E39E]/20">
                <p className="text-center text-white/50 text-xs mb-4">MARCADOR FINAL</p>
                
                {/* Países arriba */}
                <div className="flex items-center justify-between mb-4">
                  {/* País Home */}
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <img src={FLAG_URL(selectedMatch.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold text-sm text-center">{selectedMatch.home_team?.name}</span>
                  </div>
                  
                  {/* VS en medio */}
                  <span className="text-white/30 font-bold text-xl px-6">VS</span>
                  
                  {/* País Away */}
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <img src={FLAG_URL(selectedMatch.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold text-sm text-center">{selectedMatch.away_team?.name}</span>
                  </div>
                </div>
                
                {/* Marcadores debajo */}
                <div className="flex items-center justify-between">
                  {/* Marcador Home */}
                  <div className="flex-1 flex justify-center">
                    <span className="text-white font-black text-4xl" style={{ fontFamily: 'Montserrat' }}>
                      {results[selectedMatch.id]?.home_goals ?? '-'}
                    </span>
                  </div>
                  
                  {/* Guion separador */}
                  <span className="text-white/30 font-bold text-3xl px-6">-</span>
                  
                  {/* Marcador Away */}
                  <div className="flex-1 flex justify-center">
                    <span className="text-white font-black text-4xl" style={{ fontFamily: 'Montserrat' }}>
                      {results[selectedMatch.id]?.away_goals ?? '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tu Predicción */}
              {predictions[selectedMatch.id] ? (
                <div className="bg-[#0A0F1C]/50 rounded-lg p-4 border border-white/10">
                  <p className="text-center text-white/50 text-xs mb-3">TU PREDICCIÓN</p>
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-white/70 font-bold text-xl">
                      {predictions[selectedMatch.id].home_goals}
                    </span>
                    <span className="text-white/30">-</span>
                    <span className="text-white/70 font-bold text-xl">
                      {predictions[selectedMatch.id].away_goals}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20 text-center">
                  <p className="text-red-400 text-sm">No realizaste predicción para este partido</p>
                </div>
              )}

              {/* Puntos Ganados */}
              {predictions[selectedMatch.id] && (
                <div className="bg-gradient-to-r from-[#F4E39E]/20 to-[#926F39]/20 rounded-lg p-4 border border-[#F4E39E]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="text-[#F4E39E]" size={20} />
                      <span className="text-white font-semibold">Puntos Ganados:</span>
                    </div>
                    <span className="text-[#F4E39E] font-black text-2xl" style={{ fontFamily: 'Montserrat' }}>
                      {calculatePoints(predictions[selectedMatch.id], results[selectedMatch.id])} pts
                    </span>
                  </div>
                  {calculatePoints(predictions[selectedMatch.id], results[selectedMatch.id]) === 3 && (
                    <p className="text-green-400 text-xs mt-2 text-center">🎯 ¡Marcador exacto!</p>
                  )}
                  {calculatePoints(predictions[selectedMatch.id], results[selectedMatch.id]) === 1 && (
                    <p className="text-blue-400 text-xs mt-2 text-center">✅ Resultado correcto</p>
                  )}
                  {calculatePoints(predictions[selectedMatch.id], results[selectedMatch.id]) === 0 && (
                    <p className="text-red-400 text-xs mt-2 text-center">❌ No acertaste</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
